import { useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { useAtomValue } from 'jotai';
import {
    isLoggedIn,
    handleSsoCallback,
    redirectToSsoLogin,
    getAccessToken,
    clearTokens
} from 'common/utils/auth';
import { userStore } from 'store/userStore';
import { getUserInfo, UserInfo } from 'common/utils/secureStorage';

/**
 * 인증 상태를 관리하는 훅
 */
export const useAuth = () => {
    const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [userInfoLoaded, setUserInfoLoaded] = useState<boolean>(false);
    const location = useLocation();

    // SSO 회원정보 조회 상태
    const ssoUserState = useAtomValue(userStore.ssoUser.stateAtom);

    useEffect(() => {
        // SSO 콜백 처리 (토큰이 URL에 있는 경우)
        const callbackHandled = handleSsoCallback();

        if (callbackHandled) {
            setIsAuthenticated(true);
            // SSO 콜백 후 회원정보 조회
            userStore.onGetSsoUser();
            setIsLoading(false);
            return;
        }

        // 기존 토큰 확인
        const loggedIn = isLoggedIn();
        setIsAuthenticated(loggedIn);

        // 로그인 상태이고 저장된 사용자 정보가 없으면 조회
        if (loggedIn && !getUserInfo()) {
            userStore.onGetSsoUser();
        } else {
            setUserInfoLoaded(true);
        }

        setIsLoading(false);
    }, [location.search]);

    // SSO 회원정보 조회 결과 처리
    useEffect(() => {
        if (ssoUserState.status === 'ready' && ssoUserState.data) {
            // 회원정보 암호화 저장
            userStore.onSaveUserInfo(ssoUserState.data);
            setUserInfoLoaded(true);
            userStore.ssoUser.onDefault();
        } else if (ssoUserState.status === 'error') {
            console.error('Failed to fetch user info:', ssoUserState.error);
            setUserInfoLoaded(true);
            userStore.ssoUser.onDefault();
        }
    }, [ssoUserState.status, ssoUserState.data]);

    const login = (returnUrl?: string) => {
        redirectToSsoLogin(returnUrl || window.location.href);
    };

    const logout = () => {
        userStore.onLogout();
        setIsAuthenticated(false);
        setUserInfoLoaded(false);
    };

    return {
        isAuthenticated,
        isLoading,
        userInfoLoaded,
        login,
        logout,
        accessToken: getAccessToken()
    };
};

/**
 * 인증이 필요한 페이지에서 사용하는 훅
 * 로그인되어 있지 않으면 자동으로 SSO 로그인 페이지로 리다이렉트
 */
export const useRequireAuth = () => {
    const { isAuthenticated, isLoading, userInfoLoaded } = useAuth();
    const location = useLocation();

    useEffect(() => {
        if (!isLoading && !isAuthenticated) {
            // 현재 전체 URL을 returnUrl로 전달
            const returnUrl = window.location.origin + location.pathname + location.search;
            redirectToSsoLogin(returnUrl);
        }
    }, [isAuthenticated, isLoading, location]);

    return { isAuthenticated, isLoading, userInfoLoaded };
};

/**
 * 사용자 정보를 조회하는 훅
 * 복호화된 사용자 정보 반환
 */
export const useUserInfo = () => {
    const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
    const { isAuthenticated, userInfoLoaded } = useAuth();

    useEffect(() => {
        if (isAuthenticated && userInfoLoaded) {
            const info = getUserInfo();
            setUserInfo(info);
        } else {
            setUserInfo(null);
        }
    }, [isAuthenticated, userInfoLoaded]);

    return {
        userInfo,
        nickname: userInfo?.nickname || '',
        userId: userInfo?.userId || '',
        ctn: userInfo?.ctn || '',
        age: userInfo?.age || '',
        profileImgPath: userInfo?.profileImgPath || '',
        profileImgNo: userInfo?.profileImgNo || '',
        isMinor: userInfo?.isMinor || 'N',
        locale: userInfo?.locale || 'ko',
        isLoaded: userInfoLoaded
    };
};
